import {
  useQuery,
  useSubscription,
  useLazyQuery,
  useMutation,
  QueryHookOptions,
  LazyQueryHookOptions,
  MutationHookOptions,
  SubscriptionHookOptions,
} from '@apollo/client';
import ConversationOneOnOneHQQuery from './ConversationOneOnOne.hq.graphql';
import type {
  ConversationOneOnOneHQ,
  ConversationOneOnOneHQVariables,
} from './__generated__/ConversationOneOnOneHQ';
import SendCompositionHQMutation from './SendComposition.hq.graphql';
import type {
  SendCompositionHQ,
  SendCompositionHQVariables,
} from './__generated__/SendCompositionHQ';
import SendConversationMessageHQMutation from './SendConversationMessage.hq.graphql';
import type {
  SendConversationMessageHQ,
  SendConversationMessageHQVariables,
} from './__generated__/SendConversationMessageHQ';
import ConversationMessagesHQQuery from './ConversationMessages.hq.graphql';
import type {
  ConversationMessagesHQ,
  ConversationMessagesHQVariables,
  ConversationMessagesHQ_conversationMessages_messages as Message,
} from './__generated__/ConversationMessagesHQ';
import ConversationsListHQQuery from './ConversationsList.hq.graphql';
import type {
  ConversationsListHQ,
  ConversationsListHQVariables,
  ConversationsListHQ_conversationsList_items as Conversation,
} from './__generated__/ConversationsListHQ';
import ConversationsListUnreadsHQSubscription from './ConversationsListUnreads.hq.graphql';
import type {
  ConversationsListUnreadsHQ,
  ConversationsListUnreadsHQVariables,
} from './__generated__/ConversationsListUnreadsHQ';
import UserProfileForConversationsQuery from './UserProfileForConversations.gql';
import type {
  UserProfileForConversations,
  UserProfileForConversationsVariables,
} from './__generated__/UserProfileForConversations';

import ClearUnreadsHQMutation from './ClearUnreads.hq.graphql';
import type { ClearUnreadsHQ, ClearUnreadsHQVariables } from './__generated__/ClearUnreadsHQ';

import NewOneOnOneConversationHQMutation from './NewOneOnOneConversation.hq.graphql';
import type {
  NewOneOnOneConversationHQ,
  NewOneOnOneConversationHQVariables,
} from './__generated__/NewOneOnOneConversationHQ';

import ConversationUsersListHQQuery from './ConversationUsersList.hq.graphql';
import type {
  ConversationUsersListHQ,
  ConversationUsersListHQVariables,
} from './__generated__/ConversationUsersListHQ';

import LoadByGroupCodeQuery from 'groups/queries/LoadByGroupCodeQuery.gql';
import type {
  LoadGroupByCode,
  LoadGroupByCodeVariables,
  LoadGroupByCode_classes,
} from 'groups/queries/__generated__/LoadGroupByCode';

import UserProfileScreenForConversationsQuery from './UserProfileScreenForConversations.gql';
import type {
  UserProfileScreenForConversations,
  UserProfileScreenForConversationsVariables,
} from './__generated__/UserProfileScreenForConversations';

export type { ConversationMessagesHQ, ConversationMessagesHQVariables };

export * as optimistic from './optimistic';

export type { Message };

export type { ConversationOneOnOneHQ };

export type { Conversation };

export function useConversationOneOnOne(
  options: QueryHookOptions<ConversationOneOnOneHQ, ConversationOneOnOneHQVariables> = {}
) {
  return useQuery<ConversationOneOnOneHQ, ConversationOneOnOneHQVariables>(
    ConversationOneOnOneHQQuery,
    {
      fetchPolicy: 'network-only',
      context: { hq: true },
      ...options,
    }
  );
}

export function useConversationOneOnOneLazy(
  options: LazyQueryHookOptions<ConversationOneOnOneHQ, ConversationOneOnOneHQVariables> = {}
) {
  return useLazyQuery<ConversationOneOnOneHQ, ConversationOneOnOneHQVariables>(
    ConversationOneOnOneHQQuery,
    {
      fetchPolicy: 'network-only',
      context: { hq: true },
      ...options,
    }
  );
}

export type { LoadGroupByCode, LoadGroupByCode_classes as Group };

export function useOrg(options: QueryHookOptions<LoadGroupByCode, LoadGroupByCodeVariables> = {}) {
  return useQuery<LoadGroupByCode, LoadGroupByCodeVariables>(LoadByGroupCodeQuery, {
    ...options,
  });
}

export function useSendComposition(
  options: MutationHookOptions<SendCompositionHQ, SendCompositionHQVariables> = {}
) {
  return useMutation<SendCompositionHQ, SendCompositionHQVariables>(SendCompositionHQMutation, {
    context: { hq: true },
    ...options,
  });
}

export function useNewOneOnOneConversation(
  options: MutationHookOptions<NewOneOnOneConversationHQ, NewOneOnOneConversationHQVariables> = {}
) {
  return useMutation<NewOneOnOneConversationHQ, NewOneOnOneConversationHQVariables>(
    NewOneOnOneConversationHQMutation,
    {
      context: { hq: true },
      ...options,
    }
  );
}

export function useSendConversationMessage(
  options: MutationHookOptions<SendConversationMessageHQ, SendConversationMessageHQVariables> = {}
) {
  return useMutation<SendConversationMessageHQ, SendConversationMessageHQVariables>(
    SendConversationMessageHQMutation,
    {
      context: { hq: true },
      ...options,
    }
  );
}

export function useConvoMessages(
  options: QueryHookOptions<ConversationMessagesHQ, ConversationMessagesHQVariables> = {}
) {
  return useQuery<ConversationMessagesHQ, ConversationMessagesHQVariables>(
    ConversationMessagesHQQuery,
    {
      context: { hq: true },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      ...options,
    }
  );
}

export function useConvoList(
  options: QueryHookOptions<ConversationsListHQ, ConversationsListHQVariables> = {}
) {
  return useQuery<ConversationsListHQ, ConversationsListHQVariables>(ConversationsListHQQuery, {
    context: { hq: true },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    ...options,
  });
}

export function useConvoListUnreadsSubscription(
  options: SubscriptionHookOptions<
    ConversationsListUnreadsHQ,
    ConversationsListUnreadsHQVariables
  > = {}
) {
  return useSubscription<ConversationsListUnreadsHQ, ConversationsListUnreadsHQVariables>(
    ConversationsListUnreadsHQSubscription,
    {
      shouldResubscribe: true,
      context: { hq: true },
      ...options,
    }
  );
}

export function useClearUnreads(
  options: MutationHookOptions<ClearUnreadsHQ, ClearUnreadsHQVariables> = {}
) {
  return useMutation<ClearUnreadsHQ, ClearUnreadsHQVariables>(ClearUnreadsHQMutation, {
    context: { hq: true },
    ...options,
  });
}

export function useUserProfile(
  options: QueryHookOptions<UserProfileForConversations, UserProfileForConversationsVariables> = {}
) {
  return useQuery<UserProfileForConversations, UserProfileForConversationsVariables>(
    UserProfileForConversationsQuery,
    {
      // We want to avoid refetching if the data is already in the cache given the
      // most common flow is to navigate to a convo from the people's list where this will
      // already fetched and fresh enough for us
      fetchPolicy: 'cache-first',
      ...options,
    }
  );
}

export function useConversationUsersList(
  options: QueryHookOptions<ConversationUsersListHQ, ConversationUsersListHQVariables> = {}
) {
  return useQuery<ConversationUsersListHQ, ConversationUsersListHQVariables>(
    ConversationUsersListHQQuery,
    {
      context: { hq: true },
      ...options,
    }
  );
}

export function useUserProfileScreen(
  options: QueryHookOptions<
    UserProfileScreenForConversations,
    UserProfileScreenForConversationsVariables
  > = {}
) {
  return useQuery<UserProfileScreenForConversations, UserProfileScreenForConversationsVariables>(
    UserProfileScreenForConversationsQuery,
    {
      ...options,
    }
  );
}
